import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, EMAIL_API_URL } from '../../config';
import { Button, Tag, Card, Row, Col, Typography, message } from 'antd';
import { MailOutlined, SendOutlined, TeamOutlined, FileTextOutlined, EditOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const PAGE_SIZE = 25;   // rows per page in the Sent Emails history

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// MAIN PAGE
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
export default function BulkEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [stats, setStats] = useState({ total_customers: 0, monthly_sent: 0, today_sent: 0 });
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  // serverPaged=false → backend hasn't shipped pagination yet; whole history
  // arrives at once and the container simply scrolls.
  const [pagination, setPagination] = useState({ serverPaged: false, totalRecords: null, totalPages: null });

  const pollTimerRef = useRef(null);

  /**
   * Sent-email history.
   *
   * Sends page/limit as per the agreed contract. Until the backend ships
   * pagination it simply ignores them and returns its default page, so the
   * response is handled both ways:
   *   - server paginated  → response carries `pagination`, rows are used as-is
   *   - not yet paginated → whatever array comes back is sliced client-side
   * That keeps the table usable today and needs no change on deploy day.
   */
  const fetchLogs = (returnCount = false, silent = false, pageArg = 1, append = false) => {
    if (!silent && !append) setLogsLoading(true);
    if (append) setLoadingMore(true);
    const token = localStorage.getItem("cms_token");
    return axios.get(`${EMAIL_API_URL}/api/bulk-email/logs`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { page: pageArg, limit: PAGE_SIZE },
    })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        if (Array.isArray(data)) {
          // Append while scrolling, replace on a fresh load. Dedup by id so the
          // post-send polling can't insert the same row twice.
          setLogs((prev) => {
            if (!append) return data;
            const seen = new Set(prev.map((r) => r.id));
            return [...prev, ...data.filter((r) => !seen.has(r.id))];
          });
        }
        if (res.data?.stats) setStats(s => ({ ...s, ...res.data.stats }));

        const pg = res.data?.pagination;
        setPagination(
          pg
            ? {
                serverPaged: true,
                totalRecords: pg.total_records ?? (Array.isArray(data) ? data.length : 0),
                totalPages:
                  pg.total_pages ??
                  Math.max(1, Math.ceil((pg.total_records || 0) / (pg.per_page || PAGE_SIZE))),
              }
            : { serverPaged: false, totalRecords: null, totalPages: null },
        );
        return returnCount ? (Array.isArray(data) ? data.length : 0) : undefined;
      })
      .catch(() => 0)
      .finally(() => {
        if (!silent && !append) setLogsLoading(false);
        if (append) setLoadingMore(false);
      });
  };

  /* ── Infinite scroll ───────────────────────────────────────────────────────
     More pages exist only when the backend is paginating. Until then the whole
     history arrives in one response and the container just scrolls. */
  const totalRecords = pagination.serverPaged ? pagination.totalRecords : logs.length;
  const hasMore = pagination.serverPaged && page < (pagination.totalPages || 1);

  // Refs so the observer callback always sees fresh values without re-subscribing
  const hasMoreRef = useRef(hasMore);
  const loadingMoreRef = useRef(loadingMore);
  const pageRef = useRef(page);
  hasMoreRef.current = hasMore;
  loadingMoreRef.current = loadingMore;
  pageRef.current = page;

  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !loadingMoreRef.current) {
          const next = pageRef.current + 1;
          setPage(next);
          fetchLogs(false, true, next, true);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pollLogsAfterSend = (prevCount) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    let attempts = 0;
    pollTimerRef.current = setInterval(async () => {
      attempts++;
      const newCount = await fetchLogs(true, true);
      if (newCount > prevCount || attempts >= 8) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }, 2000);
  };

  useEffect(() => {
    const token = localStorage.getItem("cms_token");
    axios.get(`${API_BASE_URL}/get-email-template-list`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        if (Array.isArray(data)) setTemplates(data.filter((t) => t.status === "active"));
      })
      .catch(() => {});
    fetchLogs(false, false).then((count) => {
      if (location.state?.triggerPoll) pollLogsAfterSend(count ?? 0);
    });
  }, []); // eslint-disable-line

  const openCompose = (template = null) => {
    navigate("/send-bulk-mail/compose", { state: { template } });
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
      <style>{`
        .template-card { transition: all 0.25s ease !important; }
        .template-card:hover { transform: translateY(-3px) !important; box-shadow: 0 8px 32px rgba(48,43,99,0.14) !important; }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      `}</style>

          {/* â”€â”€ Page Header â”€â”€ */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <div>
                  <h4 className="mb-sm-0 font-size-18">Send Bulk Email</h4>
                  <p style={{ margin: 0, fontSize: 12, color: "#999" }}>Manage templates and send campaigns to your customers</p>
                </div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item"><a href="/cms/home">CMS</a></li>
                  <li className="breadcrumb-item active">Send Bulk Email</li>
                </ol>
              </div>
            </div>
          </div>

          {/* â”€â”€ Stats â”€â”€ */}
          <Row gutter={20} style={{ marginBottom: 28 }}>
            {[
              { title: "Total Customers", value: stats.total_customers || "0", icon: <TeamOutlined />, grad: "linear-gradient(135deg,#302b63,#514b9e)", light: "#f0eeff" },
              { title: "Email Templates", value: templates.length, icon: <FileTextOutlined />, grad: "linear-gradient(135deg,#0050b3,#1890ff)", light: "#e6f4ff" },
              { title: "Monthly Email Sent", value: stats.monthly_sent || "0", icon: <SendOutlined />, grad: "linear-gradient(135deg,#237804,#52c41a)", light: "#f6ffed" },
              { title: "Today Email Sent", value: stats.today_sent || "0", icon: <MailOutlined />, grad: "linear-gradient(135deg,#c0400a,#e87a4e)", light: "#fff4ee" },
            ].map((s) => (
              <Col span={6} key={s.title}>
                <Card
                  style={{ borderRadius: 18, border: "none", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", overflow: "hidden" }}
                  bodyStyle={{ padding: 0 }}
                >
                  <div style={{ padding: "20px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6 }}>{s.title}</div>
                      <div style={{ fontSize: 34, fontWeight: 800, color: "#1a1a2e", lineHeight: 1 }}>{s.value}</div>
                    </div>
                    <div style={{ width: 52, height: 52, borderRadius: 16, background: s.grad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", boxShadow: `0 6px 16px ${s.light}` }}>
                      {s.icon}
                    </div>
                  </div>
                  <div style={{ height: 4, background: s.grad }} />
                </Card>
              </Col>
            ))}
          </Row>

          {/* â”€â”€ Sent Emails Header â”€â”€ */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <Title level={5} style={{ margin: 0, color: "#1a1a2e" }}>Sent Emails</Title>
              <Text style={{ color: "#aaa", fontSize: 12 }}>History of bulk emails sent to customers</Text>
            </div>
            <Button
              type="primary"
              size="large"
              icon={<EditOutlined />}
              onClick={() => openCompose(null)}
              style={{
                background: "linear-gradient(135deg, #302b63, #24243e)",
                border: "none", borderRadius: 12, fontWeight: 600,
                padding: "0 26px", boxShadow: "0 4px 16px rgba(48,43,99,0.35)",
              }}
            >
              Compose Email
            </Button>
          </div>

          {/* â”€â”€ Sent Emails Table â”€â”€ */}
          <Card style={{ borderRadius: 18, border: "none", boxShadow: "0 4px 24px rgba(0,0,0,0.07)", overflow: "hidden" }} bodyStyle={{ padding: 0 }}>
            {/* Scroll container — rows load as the user scrolls to the bottom */}
            <div style={{ maxHeight: "62vh", overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "linear-gradient(135deg,#302b63,#3d3580)" }}>
                  {["#", "Customer", "Email", "Subject", "Template", "Sent At", "Status"].map((h) => (
                    <th key={h} style={{ padding: "14px 18px", textAlign: "left", fontWeight: 600, color: "rgba(255,255,255,0.85)", fontSize: 12, letterSpacing: "0.4px", position: "sticky", top: 0, zIndex: 1, background: "#332d6b" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logsLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f0f0f8" }}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} style={{ padding: "14px 18px" }}>
                          <div style={{ height: 14, borderRadius: 6, background: "linear-gradient(90deg,#f0f0f8 25%,#e8e8f0 50%,#f0f0f8 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite", width: j === 0 ? 28 : j === 1 ? 120 : j === 2 ? 160 : j === 3 ? 140 : j === 4 ? 80 : j === 5 ? 100 : 60 }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : logs.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: "32px", textAlign: "center", color: "#bbb", fontSize: 13 }}>No emails sent yet</td></tr>
                ) : logs.map((row, i) => {
                  const statusKey = (row.status || "pending").toLowerCase();
                  const statusCfg = {
                    sent:      { bg: "#f6ffed", border: "#b7eb8f", color: "#389e0d", dot: "#52c41a", label: "Sent" },
                    delivered: { bg: "#f6ffed", border: "#b7eb8f", color: "#389e0d", dot: "#52c41a", label: "Delivered" },
                    failed:    { bg: "#fff2f0", border: "#ffccc7", color: "#cf1322", dot: "#ff4d4f", label: "Failed" },
                    pending:   { bg: "#fffbe6", border: "#ffe58f", color: "#d48806", dot: "#faad14", label: "Pending" },
                  }[statusKey] || { bg: "#fffbe6", border: "#ffe58f", color: "#d48806", dot: "#faad14", label: statusKey };
                  const avatarColors = ["#302b63","#0050b3","#237804","#c0400a","#7b3f9e","#0e7490"];
                  const name = row.customer_name || "—";
                  const tmplName = templates.find((t) => String(t.id) === String(row.template_id))?.template_name || null;
                  const sentAt = row.sent_at ? new Date(row.sent_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                  return (
                    <tr key={row.id || i}
                      style={{ borderBottom: "1px solid #f0f0f8", background: i % 2 === 0 ? "#fff" : "#fafbff", transition: "background 0.15s" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f5f3ff"}
                      onMouseLeave={(e) => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafbff"}
                    >
                      <td style={{ padding: "14px 18px", color: "#bbb", fontSize: 12, fontWeight: 600 }}>{String(i + 1).padStart(2,"0")}</td>
                      <td style={{ padding: "14px 18px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 10, background: `${avatarColors[i % avatarColors.length]}18`, color: avatarColors[i % avatarColors.length], display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                            {name[0]}
                          </div>
                          <span style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 13 }}>{name}</span>
                        </div>
                      </td>
                      <td style={{ padding: "14px 18px", color: "#6366f1", fontSize: 12 }}>{row.customer_email || "—"}</td>
                      <td style={{ padding: "14px 18px", color: "#374151", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.subject || "—"}</td>
                      <td style={{ padding: "14px 18px" }}>
                        {tmplName
                          ? <Tag style={{ borderRadius: 20, fontSize: 11, margin: 0, padding: "2px 10px", background: "#f0eeff", border: "1px solid #c8c5f0", color: "#302b63", fontWeight: 500 }}>{tmplName}</Tag>
                          : <span style={{ color: "#bbb", fontSize: 12 }}>—</span>
                        }
                      </td>
                      <td style={{ padding: "14px 18px", color: "#9ca3af", fontSize: 12 }}>{sentAt}</td>
                      <td style={{ padding: "14px 18px" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 20, background: statusCfg.bg, border: `1px solid ${statusCfg.border}` }}>
                          <div style={{ width: 6, height: 6, borderRadius: "50%", background: statusCfg.dot }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: statusCfg.color }}>{statusCfg.label}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Sentinel — must live inside the scroll container */}
            <div ref={sentinelRef} style={{ height: 1 }} />

            {loadingMore && (
              <div style={{ padding: "14px 0", textAlign: "center", color: "#9ca3af", fontSize: 12 }}>
                Loading more…
              </div>
            )}
            {!logsLoading && !loadingMore && logs.length > 0 && !hasMore && totalRecords > PAGE_SIZE && (
              <div style={{ padding: "14px 0", textAlign: "center", color: "#c8c5f0", fontSize: 12 }}>
                All {totalRecords} emails loaded
              </div>
            )}
            </div>
          </Card>

        </div>
      </div>
    </div>
  );
}
